import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { MapPin, Calendar as Cal, Search, Lock, Tag, Plus } from "lucide-react";
import CountryFlag from "@/components/CountryFlag";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;


export default function EventsList() {
  const { lang, t } = useLang();
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [format, setFormat] = useState("all");
  const [q, setQ] = useState("");
  const [pricing, setPricing] = useState("all");
  const [sort, setSort] = useState("date-asc");

  const DATE_LOCALES = { fr: "fr-FR", en: "en-US", es: "es-ES", it: "it-IT", pt: "pt-PT" };

  useEffect(() => {
    const [s, o] = sort.split("-");
    const params = { when: tab, lang, sort: s, order: o };
    if (format !== "all") params.format = format;
    if (pricing !== "all") params.pricing = pricing;
    if (q.trim()) params.q = q.trim();
    const tmo = setTimeout(() => {
      api.get("/events", { params }).then((r) => setEvents(r.data)).catch(() => {});
    }, q ? 300 : 0);
    return () => clearTimeout(tmo);
  }, [tab, format, pricing, q, lang, sort]);

  return (
    <PublicLayout>
      <Seo title={t("events_seo_title")} description={t("events_seo_desc")} path="/events" />
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="h-1 w-12 rounded-full bg-wine-500" />
            <h1 className="mt-3 text-4xl font-bold text-navy md:text-5xl">{t("events_title")}</h1>
            <p className="mt-3 max-w-2xl text-neutral-600">{t("events_subtitle")}</p>
          </div>
          <Link to="/events/publier" className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-wine-200/50 hover:bg-wine-600 hover:scale-105 transition-all" data-testid="publish-event-btn">
            <Plus className="h-4 w-4" /> Publier un événement
          </Link>
        </div>

        <div className="mb-6 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("comp_eventslist_90")} className="w-full rounded-full border border-neutral-200 bg-white pl-10 pr-4 py-2 text-sm focus:border-wine-400 focus:outline-none" data-testid="events-search" />
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1">
              {["upcoming", "past"].map((tk) => (
                <button
                  key={tk}
                  onClick={() => setTab(tk)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${tab === tk ? "bg-wine-500 text-white shadow-sm" : "text-neutral-600 hover:text-wine-600"}`}
                  data-testid={`events-tab-${tk}`}
                >
                  {tk === "upcoming" ? t("events_upcoming") : t("events_past")}
                </button>
              ))}
            </div>
            <select value={format} onChange={(e) => setFormat(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="events-filter-format">
              <option value="all">{t("events_all_formats")}</option>
              <option value="physical">{t("events_format_physical")}</option>
              <option value="digital">{t("events_format_digital")}</option>
              <option value="hybrid">{t("events_format_hybrid")}</option>
            </select>
            <select value={pricing} onChange={(e) => setPricing(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="events-filter-pricing">
              <option value="all">Tarif</option>
              <option value="free">Gratuit</option>
              <option value="paid">Payant</option>
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="events-sort">
              <option value="date-asc">{t("comp_eventslist_91")}</option>
              <option value="date-desc">{t("comp_eventslist_92")}</option>
              <option value="title-asc">Titre A → Z</option>
              <option value="title-desc">Titre Z → A</option>
              <option value="created_at-desc">{t("comp_eventslist_93")}</option>
              <option value="created_at-asc">{t("comp_eventslist_94")}</option>
            </select>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {events.map((e, i) => {
            const isPaid = e.pricing?.is_paid || (e.tickets || []).some((t) => (t.price || 0) > 0);
            return (
            <Link key={e.id} to={`/events/${e.slug}`} className="group overflow-hidden rounded-2xl border border-neutral-100 bg-white transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`event-card-${i}`}>
              <div className="relative aspect-[16/9] overflow-hidden bg-wine-50">
                <img src={resolveUrl(e.image)} alt={e.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {e.is_cancelled && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40" data-testid={`event-cancelled-${i}`}>
                    <div className="-rotate-12 transform rounded-lg border-4 border-rose-600 bg-white/95 px-4 py-1.5 shadow-xl">
                      <div className="text-base font-black uppercase tracking-widest text-rose-600">Annulé</div>
                    </div>
                  </div>
                )}
                <span className="absolute top-3 left-3 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-wine-700 backdrop-blur">{e.format}</span>
                <div className="absolute top-3 right-3 flex flex-col gap-1.5">
                  {(() => {
                    // J-X countdown badge — visible only for upcoming events within 1 month.
                    if (!e.date) return null;
                    const ms = new Date(e.date).getTime() - Date.now();
                    if (ms < 0) return null;
                    const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
                    if (days > 30) return null;
                    const cls = days <= 7 ? "bg-rose-500" : days <= 14 ? "bg-amber-500" : "bg-wine-600";
                    return (
                      <span className={`inline-flex items-center gap-1 rounded-full ${cls} px-2 py-0.5 text-[10px] font-bold uppercase text-white shadow`} data-testid={`event-countdown-${i}`}>
                        ⏳ {days === 0 ? "Jour J" : `J-${days}`}
                      </span>
                    );
                  })()}
                  {e.audience === "members_only" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white shadow"><Lock className="h-2.5 w-2.5" />Membres</span>}
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase shadow ${isPaid ? "bg-wine-600 text-white" : "bg-emerald-500 text-white"}`}><Tag className="h-2.5 w-2.5" />{isPaid ? "Payant" : "Gratuit"}</span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs text-neutral-500">
                  <span className="inline-flex items-center gap-1"><Cal className="h-3 w-3" /> {new Date(e.date).toLocaleDateString(DATE_LOCALES[lang] || "fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location}</span>
                  {e.country && <CountryFlag country={e.country} size="xs" />}
                </div>
                <h3 className="mt-3 font-serif text-xl text-neutral-900 group-hover:text-wine-700">{e.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-neutral-600">{e.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="rounded-full bg-wine-50 px-2.5 py-1 text-xs font-semibold text-wine-700">{e.type}</span>
                  <span className="text-xs text-neutral-500">{e.registered || 0} {t("events_registered")}</span>
                </div>
              </div>
            </Link>
          );})}
          {events.length === 0 && (
            <div className="col-span-full rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/50 px-6 py-16 text-center text-sm text-neutral-500" data-testid="events-empty">
              Aucun événement{q ? ` pour "${q}"` : ""}.
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
